import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';

export type TTreatmentValues = {
  preAi: {
    treatmentType: 'FILLINGS' | 'CROWNS' | 'SRP';
    totalValuePerPatient: number;
  }[];
  recapPeriod: {
    treatmentType: 'FILLINGS' | 'CROWNS' | 'SRP';
    totalValuePerPatient: number;
  }[];
};

export const useGetTreatmentValues = (
  options?: UseQueryOptions<TTreatmentValues, unknown, TTreatmentValues>,
) => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector((state: any) => state.login.user);
  const path = `${endpoints.metrics}/${endpoints.recap}/${currentOrg}/practice/${currentPractice}/${endpoints.treatmentValues}`;

  return useQuery<TTreatmentValues>(
    [path],
    () => makeAPIRequestReactQuery<TTreatmentValues>(path),
    { enabled: !!currentPractice, ...options },
  );
};

type TIncompleteTreatments = {
  caseAcceptanceRate: number;
  distinctPatients: number;
  treatmentsPlannedNotCompleted: {
    treatmentType: 'FILLINGS' | 'CROWNS' | 'SRP';
    totalTreatments: number;
  }[];
};

export const useGetIncompleteTreatments = (
  options?: UseQueryOptions<
    TIncompleteTreatments,
    unknown,
    TIncompleteTreatments
  >,
) => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector((state: any) => state.login.user);
  const path = `${endpoints.metrics}/${endpoints.recap}/${currentOrg}/practice/${currentPractice}/${endpoints.incompleteTreatments}`;

  return useQuery<TIncompleteTreatments>(
    [path],
    () => makeAPIRequestReactQuery<TIncompleteTreatments>(path),
    { enabled: !!currentPractice, ...options },
  );
};

type TPatientFindings = {
  postAi: {
    patientAnalyses: {
      patientsWithAnalysis: number;
      patientsWithCariesFindings: number;
    };
  };
  recapPeriod: {
    patientAnalyses: {
      patientsWithAnalysis: number;
      patientsWithCariesFindings: number;
    };
  };
};

export const useGetPatientFindings = (
  options?: UseQueryOptions<TPatientFindings, unknown, TPatientFindings>,
) => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector((state: any) => state.login.user);
  const path = `${endpoints.metrics}/${endpoints.recap}/${currentOrg}/practice/${currentPractice}/${endpoints.patientFindings}`;

  return useQuery<TPatientFindings>(
    [path],
    () => makeAPIRequestReactQuery<TPatientFindings>(path),
    { enabled: !!currentPractice, ...options },
  );
};
